// ============================================================================
// PROPERTY HOOKS
// ============================================================================
// React hooks for Property Master API operations

import { useState, useEffect, useCallback } from 'react';
import { propertyService } from 'api/services';
import type {
  Property,
  CreatePropertyRequest,
  UpdatePropertyRequest,
  PropertySearchRequest,
  ID,
} from 'api/types';

/**
 * Hook to fetch all properties (paginated)
 */
export const useProperties = (params?: PropertySearchRequest) => {
  const [data, setData] = useState<Property[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  // Serialize params to avoid infinite re-render when caller passes an object literal.
  const paramsKey = JSON.stringify(params);

  const fetchProperties = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await propertyService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchProperties,
  };
};

/**
 * Hook to fetch a single property by ID
 */
export const useProperty = (id: ID) => {
  const [data, setData] = useState<Property | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchProperty = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const response = await propertyService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  return {
    data,
    loading,
    error,
    refetch: fetchProperty,
  };
};

/**
 * Hook to create a new property
 */
export const useCreateProperty = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createProperty = async (
    data: CreatePropertyRequest,
    options?: {
      onSuccess?: (property: Property) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await propertyService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createProperty,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a property
 */
export const useUpdateProperty = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateProperty = async (
    id: ID,
    data: UpdatePropertyRequest,
    options?: {
      onSuccess?: (property: Property) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await propertyService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateProperty,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a property
 */
export const useDeleteProperty = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteProperty = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await propertyService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteProperty,
    loading,
    error,
    success,
  };
};
